import { formatIncomeSplitPercentagesNew } from '@fruitful/income-split';
import {
    editableMoneyMapAtom,
    getCategorizedRulesAndTotals,
    isMoneyMapIncomeSplitEligible,
    MoneyMapCategorySectionRedesign,
    MoneyMapEditableRulesList,
    MoneyMapFilterState,
    type MoneyMapRedesignMode,
    MoneyMapVisual,
} from '@fruitful/money-map';
import {
    MoneyMapCategory,
    MoneyMapRuleType,
    type MoneyMapRuleWithAccountDetails,
    MoneyMapStatus,
    type MoneyMapWithRulesAndAccounts,
    PaycheckRoutingRuleType,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { useQueryClient } from '@tanstack/react-query';
import { Redirect, router } from 'expo-router';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard } from 'react-native';
import {
    ArrowSplit,
    BodyLargeRegular,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    HistoryIcon,
    Icon,
    isWeb,
    KeyboardCompatibleBottomSheetTextInput,
    LabelRegular,
    Sheet,
    SmoothieScrollView,
    StyledBodyMediumRegularInputNoOutline,
    tokens,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { invalidateIncomeSplitAndMoneyMapQueries } from '../../../banking/hooks/income-split/invalidateIncomeSplitAndMoneyMapQueries';
import { incomeSplitAtom } from '../../../banking/state/PaycheckRoutingAtoms';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import FetchError from '../../../http/FetchError';
import { captureException, captureWarning } from '../../../logger/Logger';
import { useGetMembershipMoneyMapAccounts } from '../../hooks/useGetMembershipMoneyMapAccounts';
import {
    buildMoneyMapMemberUpdateRequest,
    useUpdateMoneyMapForMember,
    writeMoneyMapMemberUpdateCaches,
} from '../../hooks/useUpdateMoneyMapForMember';
import { IncomeSplitWidget } from './IncomeSplitWidget';
import { MoneyMapConfirmSheet } from './MoneyMapConfirmSheet';
import { MoneyMapVisualExplainerRedesign } from './MoneyMapVisualExplainerRedesign';

export type { MoneyMapRedesignMode };

interface MoneyMapRedesignProps {
    moneyMap?: MoneyMapWithRulesAndAccounts;
    showSubmitButton?: boolean;
    submitButtonText?: string;
    onPressSubmit?: () => void;
    onConfirmDone?: () => void;
    confirmSheetOpen?: boolean;
    onConfirmSheetClose?: () => void;
    onValidationChange?: (hasErrors: boolean) => void;
    mode?: MoneyMapRedesignMode;
    contentAboveVisual?: ReactNode;
    hasPreviousMoneyMaps: boolean;
}

/**
 * Development version of the Money Map page body.
 *
 * This intentionally starts as a copy of MoneyMap so the redesign can evolve
 * without changing the production workflow while comparisons are in progress.
 */
export const MoneyMapRedesign = ({
    moneyMap,
    showSubmitButton,
    submitButtonText,
    onPressSubmit,
    onConfirmDone,
    confirmSheetOpen,
    onConfirmSheetClose,
    onValidationChange,
    mode = 'edit-mode',
    contentAboveVisual,
    hasPreviousMoneyMaps,
}: MoneyMapRedesignProps) => {
    const { data: accounts, isLoading: isLoadingAccounts } =
        useGetMembershipMoneyMapAccounts({});
    const user = useAuthedUser();
    const accountHolders = useMemo(
        () => [
            {
                id: user.id,
                firstName: user.firstName,
                lastName: user.lastName,
            },
            ...(user.partnerMember?.id != null
                ? [
                      {
                          id: user.partnerMember.id,
                          firstName: user.partnerMember.firstName,
                          lastName: user.partnerMember.lastName,
                      },
                  ]
                : []),
        ],
        [
            user.id,
            user.firstName,
            user.lastName,
            user.partnerMember?.id,
            user.partnerMember?.firstName,
            user.partnerMember?.lastName,
        ],
    );
    const queryClient = useQueryClient();
    const toast = useToastController();
    const { mutateAsync: updateMoneyMap } = useUpdateMoneyMapForMember();
    const { value: canAddGoals } = useFeatureGate(
        FeatureFlags.ENABLE_MONEY_SYTEM_NEW_GOALS,
    );

    const [filter, setFilter] = useState(MoneyMapFilterState.ALL);
    const [filterMode, setFilterMode] = useState(mode);
    const [editableMoneyMap, setEditableMoneyMap] =
        useAtom(editableMoneyMapAtom);
    const incomeSplit = useAtomValue(incomeSplitAtom);
    const setIncomeSplit = useSetAtom(incomeSplitAtom);

    const isEditActive = mode === 'edit-mode-active';
    const prevModeRef = useRef(mode);
    // Lets IncomeSplitWidget's unmount cleanup tell whether this edit session's
    // overall Save actually completed, so it knows whether to revert its
    // pending income type/accounts change or leave it (see IncomeSplitWidget.tsx).
    const hasSavedIncomeSplitRef = useRef(false);

    // Reset bar selection synchronously when entering/leaving edit so the
    // visual never paints a drilled-in frame during the mode handoff.
    if (filterMode !== mode) {
        setFilterMode(mode);
        setFilter(MoneyMapFilterState.ALL);
    }

    const [isIncomeEditOpen, setIsIncomeEditOpen] = useState(false);
    const [isConfirmSheetOpen, setIsConfirmSheetOpen] = useState(false);
    const [incomeInput, setIncomeInput] = useState('');
    // Covers mutation + refetch + mode exit (react-query isPending ends too early).
    const [isSaving, setIsSaving] = useState(false);

    const handleConfirmSave = async () => {
        if (editableMoneyMap == null || isSaving) {
            return;
        }

        const incomeSplitSettings = {
            onlyExecuteOnPaychecks: incomeSplit?.onlyExecuteOnPaychecks ?? true,
            accountIds:
                incomeSplit?.accounts.map((account) => account.accountId) ?? [],
        };

        const body = buildMoneyMapMemberUpdateRequest({
            moneyMap: editableMoneyMap,
            incomeSplitSettings,
            // Frozen on the editable copy when edit mode opened — not the live
            // prop, which may refetch to a newer version mid-edit.
            expectedUpdatedAt: editableMoneyMap.updatedAt,
        });

        setIsSaving(true);
        try {
            const result = await updateMoneyMap({ body });

            if (result.incomeSplit != null) {
                setIncomeSplit(
                    formatIncomeSplitPercentagesNew(result.incomeSplit),
                );
            }

            writeMoneyMapMemberUpdateCaches(
                queryClient,
                result.moneyMap,
                body.moneyMapId,
            );

            if (result.moneyMap.id !== body.moneyMapId) {
                router.replace({
                    pathname: '/roadmap/money-map',
                    params: { moneyMapId: result.moneyMap.id },
                });
            }

            void invalidateIncomeSplitAndMoneyMapQueries(queryClient);

            setEditableMoneyMap(null);
            hasSavedIncomeSplitRef.current = true;
            // Exit edit mode while the sheet still covers the page so the
            // interactive view-mode chrome doesn't flash underneath.
            onConfirmDone?.();
            setIsConfirmSheetOpen(false);
            onConfirmSheetClose?.();
            toast.show('Money Map updated');
            // Leave isSaving true through close animation; reset on next open.
        } catch (error) {
            setIsSaving(false);
            captureException(error);

            if (error instanceof FetchError && error.status === 409) {
                toast.show(
                    'Your Money Map was updated elsewhere. Reload and try again.',
                );
                await invalidateIncomeSplitAndMoneyMapQueries(queryClient);
                return;
            }

            toast.show(
                error instanceof Error
                    ? error.message
                    : 'Error updating Money Map',
            );
        }
    };

    const hasValidationErrors = useMemo(() => {
        if (!editableMoneyMap) return false;

        const rules = editableMoneyMap.moneyMapRules;
        const groups = editableMoneyMap.moneyMapRuleGroups;

        for (const group of groups) {
            const groupRules = rules.filter(
                (r) => r.moneyMapRuleGroupId === group.id,
            );
            if (groupRules.length === 0) return true;
            const total = groupRules.reduce(
                (sum, r) => sum + (Number(r.percentage) || 0),
                0,
            );
            if (Math.abs(total - 1) > 0.001) return true;
        }

        const billSpendRules = rules.filter(
            (r) =>
                r.type === MoneyMapRuleType.BILLS ||
                r.type === MoneyMapRuleType.SPEND,
        );
        const totalBillsSpend = billSpendRules.reduce((sum, rule) => {
            if (
                rule.routingRuleType === PaycheckRoutingRuleType.MINIMUM_BALANCE
            ) {
                return sum + (rule.targetBalanceCents ?? 0);
            }
            if (rule.routingRuleType === PaycheckRoutingRuleType.PERCENTAGE) {
                return sum + (rule.monthlyContributionMaxCents ?? 0);
            }
            return (
                sum +
                (rule.targetBalanceCents ??
                    rule.monthlyContributionMaxCents ??
                    0)
            );
        }, 0);
        if (totalBillsSpend > editableMoneyMap.monthlyIncomeCents) return true;

        return false;
    }, [editableMoneyMap]);

    useEffect(() => {
        onValidationChange?.(hasValidationErrors);
    }, [hasValidationErrors, onValidationChange]);

    useEffect(() => {
        if (confirmSheetOpen === true) {
            setIsSaving(false);
        }
    }, [confirmSheetOpen]);

    const THOUSANDS_REGEX = /\B(?=(\d{3})+(?!\d))/g;

    const handleOpenIncomeEdit = () => {
        const currentIncome =
            editableMoneyMap?.monthlyIncomeCents ??
            moneyMap?.monthlyIncomeCents ??
            0;
        const dollars = Math.floor(currentIncome / 100);
        setIncomeInput(
            dollars > 0
                ? `$${dollars.toString().replace(THOUSANDS_REGEX, ',')}`
                : '$',
        );
        setIsIncomeEditOpen(true);
    };

    const handleSaveIncome = () => {
        const cleaned = incomeInput.replace(/[^0-9]/g, '');
        const cents = cleaned ? parseInt(cleaned, 10) * 100 : 0;
        if (cents > 0) {
            setEditableMoneyMap((prev) => {
                if (!prev) return prev;
                return { ...prev, monthlyIncomeCents: cents };
            });
        }
        setIsIncomeEditOpen(false);
    };

    useEffect(() => {
        const wasEditActive = prevModeRef.current === 'edit-mode-active';
        if (isEditActive && moneyMap) {
            setEditableMoneyMap(moneyMap);
            // Fresh edit session: arm the revert-by-default behavior in
            // IncomeSplitWidget until (and unless) this session's Save succeeds.
            hasSavedIncomeSplitRef.current = false;
        } else if (wasEditActive && !isEditActive) {
            setEditableMoneyMap(null);
        }
        prevModeRef.current = mode;
    }, [isEditActive, moneyMap, mode, setEditableMoneyMap]);

    const activeMoneyMap =
        isEditActive && editableMoneyMap ? editableMoneyMap : moneyMap;

    const {
        billRules,
        spendRules,
        goalRules,
        otherAccountRules,
        billsTotalCents,
        spendTotalCents,
        goalsTotalCents,
    } = useMemo(() => {
        return getCategorizedRulesAndTotals(activeMoneyMap, accounts);
    }, [JSON.stringify(accounts), JSON.stringify(activeMoneyMap)]);

    if (isLoadingAccounts) {
        return <FullScreenSpinner />;
    }

    if (moneyMap == null || accounts == null) {
        captureWarning('Could not fetch money map and account data');
        if (mode === 'archive-view') {
            return <Redirect href="/roadmap/previous-money-maps" />;
        }
        return <Redirect href="/roadmap/go/start" />;
    }

    const showBills =
        billRules != null &&
        billsTotalCents != null &&
        (filter === MoneyMapFilterState.ALL ||
            filter === MoneyMapFilterState.BILLS);
    const showSpend =
        spendRules != null &&
        spendTotalCents != null &&
        (filter === MoneyMapFilterState.ALL ||
            filter === MoneyMapFilterState.SPEND);
    const showGoals =
        goalRules != null &&
        goalsTotalCents != null &&
        (filter === MoneyMapFilterState.ALL ||
            filter === MoneyMapFilterState.GOALS);
    const showOtherAccounts =
        otherAccountRules.length > 0 && filter === MoneyMapFilterState.ALL;

    const isIncomeSplitEligible = isMoneyMapIncomeSplitEligible(
        activeMoneyMap ?? moneyMap,
        accounts,
    );

    const isReorderable = true;

    // On native with reordering enabled, the draggable list is the page's
    // scroller (so drag auto-scroll and measurements are the library's own),
    // and everything above and below it is passed in as list header and footer.
    const isListScroller = !isWeb && isEditActive && isReorderable;

    const visualSection = (
        <YStack gap={4}>
            <MoneyMapVisual
                // Remount when the map version changes (e.g. after save)
                // so reanimated SVG connector paths don't ghost.
                key={`${(activeMoneyMap ?? moneyMap).id}-${(activeMoneyMap ?? moneyMap).updatedAt}`}
                incomeTotalCents={
                    (activeMoneyMap ?? moneyMap).monthlyIncomeCents
                }
                billsTotalCents={billsTotalCents}
                spendTotalCents={spendTotalCents}
                goalsTotalCents={goalsTotalCents}
                filter={filter}
                setFilter={setFilter}
                isIncomeSplitEligible={isIncomeSplitEligible}
                mode={mode}
                onEditRequest={(category) => {
                    if (category === 'income') {
                        handleOpenIncomeEdit();
                    }
                }}
            />
            {isIncomeSplitEligible && !isEditActive && (
                <MoneyMapVisualExplainerRedesign filter={filter} />
            )}
        </YStack>
    );

    const editHeaderSection = (
        <>
            <IncomeSplitWidget hasMoneyMapSavedRef={hasSavedIncomeSplitRef} />
            <YStack alignItems="center">
                <ArrowSplit />
            </YStack>
        </>
    );

    const pageFooter = (
        <>
            {mode === 'edit-mode' &&
                moneyMap.status !== MoneyMapStatus.PREVIEW &&
                hasPreviousMoneyMaps && (
                    <View
                        alignSelf="center"
                        marginTop={24}
                        marginBottom={8}
                        onPress={() => {
                            router.push('/roadmap/previous-money-maps');
                        }}
                        backgroundColor="$gray200"
                        paddingVertical={12}
                        paddingHorizontal={20}
                        borderRadius={100}
                        cursor="pointer"
                        animation="button"
                        animateOnly={['transform', 'backgroundColor']}
                        pressStyle={{ scale: 0.95 }}
                    >
                        <XStack gap={8} alignItems="center">
                            <Icon
                                icon={HistoryIcon}
                                size={20}
                                color="$gray1200"
                            />
                            <BodyMediumRegular>
                                Previous Money Maps
                            </BodyMediumRegular>
                        </XStack>
                    </View>
                )}
            {isEditActive && (
                <YStack
                    paddingTop={24}
                    paddingBottom={37}
                    gap={16}
                    alignItems="center"
                >
                    <YStack width="100%" gap={12}>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Save"
                            disabled={hasValidationErrors}
                            onPress={() => {
                                setIsSaving(false);
                                setIsConfirmSheetOpen(true);
                            }}
                        />
                    </YStack>
                    <YStack gap={12} width="100%">
                        <LabelRegular color="$gray1000">
                            Fruitful is not an FDIC-insured depository
                            institution. Banking services provided by Emigrant
                            Bank, Member FDIC.
                            {'\n\n'}
                            Investment Products are not insured by the FDIC; are
                            not deposits; and may lose value.
                        </LabelRegular>
                    </YStack>
                </YStack>
            )}
            {!isEditActive &&
                showSubmitButton &&
                (filter === MoneyMapFilterState.ALL ||
                    filter === MoneyMapFilterState.GOALS) && (
                    <YStack marginBottom={24}>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text={submitButtonText ?? 'Continue'}
                            onPress={onPressSubmit}
                        />
                    </YStack>
                )}
        </>
    );

    return (
        <>
            {isListScroller ? (
                <MoneyMapEditableRulesList
                    accounts={accounts}
                    canAddGoals={canAddGoals}
                    currentUserId={user.id}
                    header={
                        <>
                            {contentAboveVisual}
                            <YStack gap={8} marginTop={24} marginBottom={16}>
                                {visualSection}
                                <YStack gap={16}>{editHeaderSection}</YStack>
                            </YStack>
                        </>
                    }
                    footer={pageFooter}
                    isReorderable={isReorderable}
                />
            ) : (
                <SmoothieScrollView
                    containerStyle={{
                        width: '100%',
                        maxWidth: 700,
                        alignSelf: 'center',
                        paddingHorizontal: 24,
                        justifyContent: 'space-between',
                        '$platform-web': {
                            marginTop: 0,
                        },
                    }}
                >
                    {contentAboveVisual}
                    <YStack gap={8} marginVertical={24}>
                        {visualSection}
                        {isEditActive ? (
                            <YStack gap={16}>
                                {editHeaderSection}
                                <MoneyMapEditableRulesList
                                    accounts={accounts}
                                    canAddGoals={canAddGoals}
                                    currentUserId={user.id}
                                    isReorderable={isReorderable}
                                />
                            </YStack>
                        ) : (
                            <>
                                {showBills && (
                                    <MoneyMapCategorySectionRedesign
                                        accountHolders={accountHolders}
                                        category={MoneyMapCategory.BILLS}
                                        moneyMap={activeMoneyMap ?? moneyMap}
                                        accounts={accounts}
                                        totalAmountCents={billsTotalCents}
                                        rules={
                                            billRules as MoneyMapRuleWithAccountDetails[]
                                        }
                                    />
                                )}
                                {showSpend && (
                                    <MoneyMapCategorySectionRedesign
                                        accountHolders={accountHolders}
                                        category={MoneyMapCategory.SPEND}
                                        moneyMap={activeMoneyMap ?? moneyMap}
                                        accounts={accounts}
                                        totalAmountCents={spendTotalCents}
                                        rules={
                                            spendRules as MoneyMapRuleWithAccountDetails[]
                                        }
                                    />
                                )}
                                {showGoals && (
                                    <MoneyMapCategorySectionRedesign
                                        accountHolders={accountHolders}
                                        category={MoneyMapCategory.GOALS}
                                        moneyMap={activeMoneyMap ?? moneyMap}
                                        accounts={accounts}
                                        totalAmountCents={goalsTotalCents}
                                        rules={
                                            goalRules as MoneyMapRuleWithAccountDetails[]
                                        }
                                        ruleVariant={
                                            mode === 'go-flow'
                                                ? 'full'
                                                : filter ===
                                                    MoneyMapFilterState.GOALS
                                                  ? 'full'
                                                  : 'summary'
                                        }
                                        onPress={() => {
                                            setFilter((prev) => {
                                                return prev ===
                                                    MoneyMapFilterState.GOALS
                                                    ? MoneyMapFilterState.ALL
                                                    : MoneyMapFilterState.GOALS;
                                            });
                                        }}
                                    />
                                )}
                                {showOtherAccounts && (
                                    <MoneyMapCategorySectionRedesign
                                        accountHolders={accountHolders}
                                        category={
                                            MoneyMapCategory.OTHER_ACCOUNTS
                                        }
                                        moneyMap={activeMoneyMap ?? moneyMap}
                                        accounts={accounts}
                                        totalAmountCents={null}
                                        rules={
                                            otherAccountRules as MoneyMapRuleWithAccountDetails[]
                                        }
                                    />
                                )}
                            </>
                        )}
                    </YStack>
                    {pageFooter}
                </SmoothieScrollView>
            )}
            <Sheet
                isOpen={isIncomeEditOpen}
                onClose={() => {
                    setIsIncomeEditOpen(false);
                }}
                showCloseButton={false}
            >
                <YStack $sm={{ minWidth: 400 }} gap={24}>
                    <XStack
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <BodyLargeRegular textAlign="center">
                            Edit take-home pay
                        </BodyLargeRegular>
                    </XStack>
                    <YStack gap={8}>
                        <LabelRegular marginLeft={8} color="$gray1100">
                            Monthly take-home pay
                        </LabelRegular>
                        <KeyboardCompatibleBottomSheetTextInput
                            component={StyledBodyMediumRegularInputNoOutline}
                            keyboardType="number-pad"
                            value={incomeInput}
                            onChangeText={(value: string) => {
                                const cleaned = value.replace(/[^0-9$,]/g, '');
                                if (!cleaned || cleaned === '$') {
                                    setIncomeInput('$');
                                    return;
                                }
                                const digits = cleaned.replace(/[^0-9]/g, '');
                                const formatted = `$${digits.replace(THOUSANDS_REGEX, ',')}`;
                                setIncomeInput(formatted);
                            }}
                            placeholder="$0"
                            returnKeyType="done"
                            onSubmitEditing={() => {
                                Keyboard.dismiss();
                            }}
                            backgroundColor={tokens.color.light.gray.gray200}
                            padding={16}
                            height={48}
                            borderWidth={1}
                            borderColor={tokens.color.light.gray.gray600}
                            borderRadius={16}
                        />
                    </YStack>
                    <XStack gap={12}>
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.LARGE}
                            text="Cancel"
                            onPress={() => {
                                setIsIncomeEditOpen(false);
                            }}
                            buttonProps={{ flexShrink: 1 }}
                        />
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            text="Save"
                            onPress={handleSaveIncome}
                            buttonProps={{ flexShrink: 1 }}
                        />
                    </XStack>
                </YStack>
            </Sheet>
            <MoneyMapConfirmSheet
                isOpen={isConfirmSheetOpen || confirmSheetOpen === true}
                onClose={() => {
                    if (isSaving) {
                        return;
                    }
                    setIsConfirmSheetOpen(false);
                    onConfirmSheetClose?.();
                }}
                onConfirm={() => {
                    void handleConfirmSave();
                }}
                isSaving={isSaving}
            />
        </>
    );
};
